<script setup>
import dayjs from 'dayjs'
import { vElementSize, vOnClickOutside } from '@vueuse/components'
import { RichTextRenderer } from '@marvr/storyblok-rich-text-vue-renderer'
import { reset } from '@formkit/core'
import { useReCaptcha } from 'vue-recaptcha-v3'

const props = defineProps({
  position: String,
  location: String,
  url: String,
  questionsDeatil: Object,
  questionsCard: Array,
  available: Array,
  multipleMail: {
    type: Array,
    default: () => [],
  },
})

const { formattedJobLocation, formattedJobTime, formattedJobCategories } = useJobCategories()

const router = useRouter()
const route = useRoute()
const fullRoute = useRoute().fullPath
const { t, locale } = useI18n()
const options = reactive([
  {
    value: t('language.en'),
  },
  {
    value: t('language.th'),
  },
])

const availableOptions = reactive(props.available)

const currencyOptions = reactive([
  {
    value: 'USD',
  },
  {
    value: 'THB',
  },
  {
    value: 'CNY',
  },
  {
    value: 'AUD',
  },
])

const planOptions = reactive([
  {
    value: 'Annually',
  },
  {
    value: 'Monthly',
  },
])

const languageOptions = computed(() => {
  return formattedJobLocation.value
    .filter((element) => element?.email) // ✅ กรองเฉพาะที่มี email
    .map((element) => ({
      value: element.name,
      email: element.email,
      icon: element.icon,
    }));
});

const planActive = ref(true)
const alertActive = ref(false)
const alertError = ref(false)
const acceptPolicy = ref(false)
const form = reactive({
  header: 'Career',
  title: 'Please review the details of your job application below.',
  subject: 'Careers - Dexon Technology PLC',
  body: '',
  firstName: '',
  lastName: '',
  email: '',
  locationEmail: '',
  phone: '',
  position: props.position,
  language: '',
  available: fullRoute === `/th/submit-cv` || fullRoute === `/submit-cv` ? availableOptions?.[0]?.value ?? '' : '',
  salary: '',
  location: '',
  tellus: '',
  cv: [],
  options: '',
  currency: currencyOptions[0].value,
})

function dropdownSwitchLanguage(event) {

  languageOptions.value?.forEach((item) => {
    if (item.value === event) {
      form.language = item.value
      form.location = item.value
      form.locationEmail = item.email
    }
  })
}

function dropdownCurrency(event) {
  form.currency = event
}

function dropdownAvailableOptions(event) {
  form.available = event
}

const routeName = route.path
const disabled = ref(false)
const uploadedFile = ref([])
const maxFileSize = ref(null)
const filesLocation = ref([])

function formatBytes(bytes, decimals = 2) {
  if (!+bytes)
    return '0 Bytes'

  const k = 1024
  const dm = decimals < 0 ? 0 : decimals
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZIB', 'YB']

  const i = Math.floor(Math.log(bytes) / Math.log(k))

  return `${Number.parseFloat((bytes / k ** i).toFixed(dm))} ${sizes[i]}`
}

function fileChange(file) {
  const fileData = file.target
  if (fileData.files.length <= 3) {
    uploadedFile.value = fileData.files
    const totalSize = Array.from(fileData.files).reduce((total, item) => total + item.size, 0)
    maxFileSize.value = totalSize > MAX_UPLOAD_BYTES
  }
  else {
    uploadedFile.value = []
    maxFileSize.value = false
  }
}

const { uploadFiles } = useUpload()
const uploadFailed = ref(false)

async function submitFiles() {
  try {
    const files = await uploadFiles(uploadedFile.value)
    filesLocation.value = files.map(item => ({ url: item.url }))
    uploadedFile.value = []
    return true
  }
  catch (err) {
    console.error(err)
    return false
  }
}

const { postCareer } = useCareer()
const getCurrentDate = ref('')
const getAllData = ref(null)

const { executeRecaptchaTokens } = useGoogleRecaptcha()
const captchaVerified = ref(false)

async function submit() {
  disabled.value = true
  const [token, replyToken, notifyToken, saveToken] = await executeRecaptchaTokens('submit', 4)

  onExport()
  uploadFailed.value = false
  if (acceptPolicy.value && form.language !== '' && form.firstName !== '' && form.lastName !== '' && form.email !== '' && form.phone !== '' && form.salary !== '' && !maxFileSize.value) {
    if (!(await submitFiles())) {
      disabled.value = false
      uploadFailed.value = true
      alertError.value = true
      captchaVerified.value = false
      return
    }

    const database = await postCareer(
      {
        token: saveToken,
        name: `${form.firstName} ${form.lastName}`,
        email: form.email,
        phone: form.phone,
        position: form.available ? form.available : form.position,
        language: form.language,
        available: form.available,
        salary: `${Number(form.salary).toLocaleString()} ${form.currency}`,
        plan: !planActive.value ? planOptions[0].value : planOptions[1].value,
        cv: filesLocation.value,
        location: form.location,
        url: routeName,
        questions: json.value,
      },
    )
    const date = new Date()
    getCurrentDate.value = dayjs(date).format('DD MMMM YYYY')
    getAllData.value = database.data.value?.number

    const verifyRecaptcha = useFetch('/api/verify-recaptcha', {
      method: 'POST',
      body: JSON.stringify({ token }),
      headers: {
        'Content-Type': 'application/json',
      },
    })

    const careerRequest = useFetch('/api/career', {
      method: 'POST',
      body: {
        token: replyToken,
        header: form.header,
        title: form.title,
        email: form.email,
        subject: form.subject,
        body: {
          available: form.available,
          firstName: form.firstName,
          lastName: form.lastName,
          email: form.email,
          phone: form.phone,
          position: form.available ? form.available : form.position,
          language: form.language,
          salary: `${Number(form.salary).toLocaleString()} ${form.currency}`,
          plan: !planActive.value ? planOptions[0].value : planOptions[1].value,
          cv: filesLocation.value,
          location: form.location,
          url: routeName,
          questions: json.value,
        },
      },
    })

    const userCareerRequest = useFetch('/api/user/career', {
      method: 'POST',
      body: {
        token: notifyToken,
        date: getCurrentDate.value,
        number: getAllData.value,
        header: form.header,
        title: form.title,
        email: form.email,
        localMail: form.locationEmail,
        locationEmail: form.locationEmail,
        multipleMail: props.multipleMail,
        subject: form.subject,
        body: {
          available: form.available,
          firstName: form.firstName,
          lastName: form.lastName,
          email: form.email,
          phone: form.phone,
          position: form.available ? form.available : form.position,
          language: form.language,
          salary: `${Number(form.salary).toLocaleString()} ${form.currency}`,
          plan: !planActive.value ? planOptions[0].value : planOptions[1].value,
          cv: filesLocation.value,
          location: form.location,
          url: routeName,
          questions: json.value,
        },
      },
    })

    const [recaptchaResponse, careerResponse, userCareerResponse] = await Promise.all([
      verifyRecaptcha,
      careerRequest,
      userCareerRequest,
    ])

    disabled.value = false

    if (recaptchaResponse.status.value === 'success' && recaptchaResponse.data.value?.success && database.status.value === 'success' && careerResponse.status.value === 'success' && userCareerResponse.status.value === 'success') {
      reset('myForm')
      json.value = []
      filesLocation.value = []
      alertActive.value = true
      captchaVerified.value = true

      setTimeout(() => {
        router.push('/thank-you')
      }, 1500)
    }
    else {
      alertError.value = true
      captchaVerified.value = false
    }
  }
  else {
    alertError.value = true
    captchaVerified.value = false
  }
}

const activeIndex = ref(0)
const answer = reactive([])

function openCard(index) {
  activeIndex.value = index
}
const getWidth = ref(0)
const getHeight = ref(0)

function onResize({ width, height }) {
  getWidth.value = width
  getHeight.value = height
}

const filteredQuestions = computed(() => {
  if (answer.length > 1)
    return answer
})

const json = ref(null)

function onExport() {
  json.value = []

  answer.forEach((data) => {
    json.value.push({
      Questions: data.text,
      Answer: data.value,
    })
  })
}
const recaptchaIns = useReCaptcha().instance

onMounted(() => {
  disabled.value = false

  if (props.questionsCard) {
    props.questionsCard.forEach((element) => {
      answer.push({
        text: element.question,
        value: '',
      })
    })
  }

  setTimeout(() => {
    recaptchaIns.value.showBadge()
  }, 1000)
})

onBeforeUnmount(() => {
  recaptchaIns.value.hideBadge()
})
</script>

<template>
  <div>
    <FormKit
      id="myForm"
      v-slot="{ disabled }"
      type="form"
      :actions="false"
      :submit-label="$t('button.submit')"
      :classes="{
        message: 'text-red-500 text-md mt-2',
      }"
      @submit="submit"
    >
      <div v-if="(route.fullPath === `/th/submit-cv` || route.fullPath === `/submit-cv`) && availableOptions?.length" class="mb-custom-md flex flex-wrap gap-4 sm:flex-nowrap">
        <div class="relative w-full cursor-pointer lg:w-1/2">
          <UIDropdown
            :options="availableOptions"
            :label="$t('career.form.available_label')"
            :selected="availableOptions?.[0]?.value"
            label-select="!normal-case"
            @on-select="dropdownAvailableOptions"
          />
        </div>
      </div>
      <div class="mb-custom-md flex flex-wrap gap-4 sm:flex-nowrap">
        <div class="w-full lg:w-1/2">
          <FormKit
            v-model="form.firstName"
            :label="`${$t('career.form.first_name')} *`"
            :placeholder="`${$t('career.form.placeholder.enter')} ${$t('career.form.first_name')}`"
            required
            name="name"
            type="text"
            validation="alpha_spaces:latin"
            validation-visibility="live"
            :input-class="{
              'formkit-input': false,
            }"
            label-class="text-md block font-medium capitalize mb-2"
            :classes="{
              message: 'text-red-500 text-sm',
              outer: 'block w-full transition duration-200',
              wrapper: '!max-w-full w-full',
              inner: `!shadow-none`,
              input: `w-full !border !border-primary/10 focus:!border-secondary-red-100/80 hover:!border-secondary-red-100/80 !placeholder-slate-300 !py-3.5 !px-4 !text-primary !shadow-sm !bg-white !text-md !rounded-md focus:!ring-offset-1 focus:!ring-2 focus:!ring-red-500/20 focus:!border-red-500`,
            }"
          />
        </div>
        <div class="w-full lg:w-1/2">
          <FormKit
            v-model="form.lastName"
            :label="`${$t('career.form.last_name')} *`"
            :placeholder="`${$t('career.form.placeholder.enter')} ${$t('career.form.last_name')}`"
            required
            name="name"
            type="text"
            validation="alpha_spaces:latin"
            validation-visibility="live"
            :input-class="{
              'formkit-input': false,
            }"
            label-class="text-md block font-medium capitalize mb-2"
            :classes="{
              message: 'text-red-500 text-sm',
              outer: 'block w-full transition duration-200',
              wrapper: '!max-w-full w-full',
              inner: `!shadow-none`,
              input: `w-full !border !border-primary/10 focus:!border-secondary-red-100/80 hover:!border-secondary-red-100/80 !placeholder-slate-300 !py-3.5 !px-4 !text-primary !shadow-sm !bg-white !text-md !rounded-md focus:!ring-offset-1 focus:!ring-2 focus:!ring-red-500/20 focus:!border-red-500`,
            }"
          />
        </div>
      </div>
      <div class="mb-custom-md flex flex-wrap gap-4 sm:flex-nowrap">
        <div class="w-full lg:w-1/2">
          <FormKit
            v-model="form.email"
            :label="`${$t('career.form.email')} *`"
            :placeholder="`${$t('career.form.placeholder.enter')} ${$t('career.form.email')}`"
            required
            type="email"
            name="email"
            validation="email"
            validation-visibility="live"
            :input-class="{
              'formkit-input': false,
            }"
            label-class="text-md block font-medium capitalize mb-2"
            :classes="{
              message: 'text-red-500 text-sm',
              outer: 'block w-full transition duration-200',
              wrapper: '!max-w-full w-full',
              inner: `!shadow-none`,
              input: `w-full !border !border-primary/10 focus:!border-secondary-red-100/80 hover:!border-secondary-red-100/80 !placeholder-slate-300 !py-3.5 !px-4 !text-primary !shadow-sm !bg-white !text-md !rounded-md focus:!ring-offset-1 focus:!ring-2 focus:!ring-red-500/20 focus:!border-red-500`,
            }"
          />
        </div>
        <div class="w-full lg:w-1/2">
          <FormKit
            v-model="form.phone"
            :label="`${$t('career.form.phone')} *`"
            :placeholder="`${$t('career.form.placeholder.enter')} ${$t('career.form.phone')}`"
            type="number"
            name="phone"
            required
            validation="number"
            validation-visibility="live"
            :input-class="{
              'formkit-input': false,
            }"
            label-class="text-md block font-medium capitalize mb-2"
            :classes="{
              message: 'text-red-500 text-sm',
              outer: 'block w-full transition duration-200',
              wrapper: '!max-w-full w-full',
              inner: `!shadow-none`,
              input: `w-full !border !border-primary/10 focus:!border-secondary-red-100/80 hover:!border-secondary-red-100/80 !placeholder-slate-300 !py-3.5 !px-4 !text-primary !shadow-sm !bg-white !text-md !rounded-md focus:!ring-offset-1 focus:!ring-2 focus:!ring-red-500/20 focus:!border-red-500`,
            }"
          />
        </div>
      </div>
      <div class="mb-custom-md flex flex-wrap gap-4 sm:flex-nowrap">
        <div class="relative w-full cursor-pointer lg:w-1/2">
          <UIDropdown
            :options="languageOptions"
            :label="$t('career.form.language')"
            :placeholder="$t('career.form.placeholder.location')"
            required
            @on-select="dropdownSwitchLanguage"
          />
        </div>
        <div class="relative w-full lg:w-1/2">
          <!-- <UIInput id="salary" v-model="form.salary" :icon="true" type="number" additional-class="py-4" :label="$t('career.form.salary')" required>
            <template #icon>
              <svg v-if="locale === 'en'" class="absolute inset-y-0 right-4 my-auto h-6 w-6 text-[#BCC2D1]" xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24"><path fill="currentColor" d="M7 15h2c0 1.08 1.37 2 3 2s3-.92 3-2c0-1.1-1.04-1.5-3.24-2.03C9.64 12.44 7 11.78 7 9c0-1.79 1.47-3.31 3.5-3.82V3h3v2.18C15.53 5.69 17 7.21 17 9h-2c0-1.08-1.37-2-3-2s-3 .92-3 2c0 1.1 1.04 1.5 3.24 2.03C14.36 11.56 17 12.22 17 15c0 1.79-1.47 3.31-3.5 3.82V21h-3v-2.18C8.47 18.31 7 16.79 7 15Z" /></svg>
              <svg v-else class="absolute inset-y-0 right-4 my-auto h-6 w-6 text-[#BCC2D1]" xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24"><path fill="currentColor" d="M16.1 11.6c.6-.7.9-1.6.9-2.6c0-1.9-1.3-3.4-3-3.9L13 5V3h-2v2H7v14h4v2h2v-2h1c2.2 0 4-1.8 4-4c0-1.5-.8-2.7-1.9-3.4M15 9c0 1.1-.9 2-2 2V7c1.1 0 2 .9 2 2M9 7h2v4H9V7m0 10v-4h2v4H9m5 0h-1v-4h1c1.1 0 2 .9 2 2s-.9 2-2 2Z" /></svg>
            </template>
          </UIInput> -->
          <label class="mb-2 block text-md font-medium capitalize">{{ t('career.form.salary') }}</label>
          <div class="flex items-center justify-center">
            <FormKit
              v-model="form.salary"
              :placeholder="`${$t('career.form.placeholder.enter')} ${$t('career.form.salary')}`"
              required
              type="number"
              name="salary"
              validation="number"
              validation-visibility="live"
              :input-class="{
                'formkit-input': false,
              }"
              :classes="{
                message: 'text-red-500 text-sm',
                outer: 'block w-full transition duration-200',
                wrapper: '!max-w-full w-full',
                inner: `!shadow-none relative`,
                input: `w-full !rounded-br-0 !rounded-tr-0 !border-r-0 !border !border-primary/10 focus:!border-secondary-red-100/80 hover:!border-secondary-red-100/80 !placeholder-slate-300 !py-3.5 !px-4 !text-primary !shadow-sm !bg-white !text-md !rounded-md focus:!ring-offset-1 focus:!ring-2 focus:!ring-red-500/20 focus:!border-red-500`,
              }"
            />
            <UIDropdown
              input-class="!mt-0 !rounded-bl-0 !rounded-tl-0 !border-l-0 !bg-secondary-red-100 [&>*]:!text-white h-[54px] cursor-pointer"
              label-select="!text-white"
              dropdown-class="!w-auto"
              :options="currencyOptions"
              :selected="currencyOptions[0].value"
              @on-select="dropdownCurrency"
            />
          </div>
          <div class="mt-custom-md">
            <label class="inline-flex cursor-pointer items-center gap-x-3">
              <span :class="{ 'text-slate-300': planActive }" class="text-sm text-primary font-medium dark:text-gray-300">Annually </span>
              <input v-model="planActive" type="checkbox" class="peer sr-only" checked>
              <div class="peer relative h-8 w-15 rounded-full bg-gray-200 after:absolute after:start-[2px] after:top-0.5 after:h-7 after:w-7 after:border after:border-gray-300 dark:border-gray-600 after:rounded-full after:bg-white dark:bg-gray-700 peer-checked:bg-secondary-red-100 peer-focus:ring-3 peer-focus:ring-red-500/20 after:transition-all after:content-[''] peer-checked:after:translate-x-full peer-checked:after:border-white dark:peer-focus:ring-secondary-red-100 rtl:peer-checked:after:-translate-x-full" />
              <span :class="{ 'text-slate-300': !planActive }" class="text-sm text-primary font-medium dark:text-gray-300">Monthly</span>
            </label>
          </div>
        </div>
      </div>
      <div class="mb-custom-md flex flex-wrap gap-4 sm:flex-nowrap">
        <div class="relative w-full">
          <FormKit
            id="upload_files"
            type="file"
            :label="$t('career.form.cv')"
            placeholder="Choose your file" name="inputFile"
            accept=".pdf,.doc,.docx"
            help="Select as many documents as you would like."
            multiple="true"
            required
            :validation="`max:3|${maxFileSize ? 'is:' : ''}`"
            :validation-messages="{
              is: `Maximum allowed is ${formatBytes(5242880)}`,
            }"
            validation-visibility="live"
            :input-class="{
              'formkit-input': false,
            }"
            prefix-icon="<svg class='absolute inset-y-0 right-4 my-auto text-[#BCC2D1]' xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24'><path fill='currentColor' d='M20.832 3.367L8.668.108a3.148 3.148 0 0 0-3.856 2.226L.943 16.777a3.148 3.148 0 0 0 2.226 3.856l12.163 3.259a3.148 3.148 0 0 0 3.855-2.226l3.87-14.443a3.148 3.148 0 0 0-2.226-3.856M6.332 2.741A1.574 1.574 0 0 1 8.26 1.628l12.163 3.26a1.574 1.574 0 0 1 1.113 1.927l-3.87 14.444a1.574 1.574 0 0 1-1.928 1.113l-12.163-3.26a1.574 1.574 0 0 1-1.113-1.927zm2.634 2.336a.787.787 0 1 0-.407 1.52l9.122 2.444a.787.787 0 1 0 .407-1.52zM6.985 9.434a.787.787 0 0 1 .963-.557l9.123 2.445a.787.787 0 0 1-.408 1.52l-9.122-2.444a.787.787 0 0 1-.556-.964m-.055 3.244a.787.787 0 0 0-.407 1.52l5.32 1.427a.787.787 0 0 0 .408-1.52z'/></svg>"
            label-class="text-md block font-medium capitalize mb-2"
            :classes="{
              help: 'font-medium text-sm mt-2 italic',
              message: 'text-red-500 text-sm',
              outer: 'block w-full transition duration-200',
              wrapper: '!max-w-full w-full formkit-custom-input',
              inner: `!shadow-none relative py-4 w-full !border !border-primary/10 focus:!border-secondary-red-100/80 hover:!border-secondary-red-100/80 !placeholder-slate-300 !py-3.5 !px-4 !text-primary !shadow-sm !bg-white !text-md !rounded-md focus:!ring-offset-1 focus:!ring-2 focus:!ring-red-500/20 focus:!border-red-500`,
              input: `absolute w-full h-full opacity-0 inset-0`,
            }"
            @change="fileChange"
          />
          <p class="mt-2">
            <span>{{ $t('career.form.upload_subtitle[0].text') }}</span> <span><b>{{ $t('career.form.upload_subtitle[1].text_bold') }}</b></span> <span>{{ $t('career.form.upload_subtitle[2].text') }}</span> <span><b>{{ $t('career.form.upload_subtitle[3].text_bold') }}</b></span>
          </p>
        </div>
      </div>
      <div class="sm:flex-nowrap">
        <div v-if="questionsCard.length || questionsDeatil.length" class="my-custom-md">
          <div
            class="overflow-hidden rounded-md transition-all duration-300 ease"
          >
            <div v-element-size="onResize">
              <div
                class="flex items-center text-md text-secondary-red-100 font-medium leading-[2] capitalize"
              >
                {{ t('career.page.question') }}
              </div>
              <h4 class="mb-custom-sm text-xl font-bold lg:text-3xl md:text-2xl">
                {{ t('career.page.question_title') }}
              </h4>
              <div v-if="questionsDeatil" class="article mb-custom-md">
                <RichTextRenderer :document="questionsDeatil" />
              </div>
            </div>
            <!-- eslint-disable -->
            <CardQuestions :blok="questionsCard">
              <template #default>
                <div 
                  v-if="filteredQuestions"
                  @click="openCard(index)" 
                  v-for="(answer, index) in filteredQuestions" :key="index" :class="activeIndex === index ? '' : 'border-white'" 
                  class="transition-all duration-300 ease-in-out border-b rounded-md"
                >
                  <h4 class="!mb-custom-sm font-medium">{{ index + 1 }}. {{ answer.text }}</h4>
                  <div class="article" v-if="questionsCard[index]?.content" v-for="detail in questionsCard" :key="detail">
                    <RichTextRenderer v-if="detail?.content" :document="detail.content" />
                  </div>
                  <FormKit
                    v-model="answer.value"
                    label=""
                    :placeholder="'Enter your answer ' + Number(index + 1)"
                    type="textarea"
                    name="answer"
                    cols="10"
                    validation=""
                    validation-visibility="live"
                    :prefix-icon="!answer.value ? `<svg class='absolute inset-y-0 right-4 my-auto text-[#BCC2D1]' xmlns='http://www.w3.org/2000/svg' width='24' height='24'><path fill='currentColor' d='M11.95 18q.525 0 .888-.363t.362-.887q0-.525-.362-.888t-.888-.362q-.525 0-.887.363t-.363.887q0 .525.363.888t.887.362m-.9-3.85h1.85q0-.825.188-1.3t1.062-1.3q.65-.65 1.025-1.238T15.55 8.9q0-1.4-1.025-2.15T12.1 6q-1.425 0-2.312.75T8.55 8.55l1.65.65q.125-.45.563-.975T12.1 7.7q.8 0 1.2.438t.4.962q0 .5-.3.938t-.75.812q-1.1.975-1.35 1.475t-.25 1.825M12 22q-2.075 0-3.9-.787t-3.175-2.138q-1.35-1.35-2.137-3.175T2 12q0-2.075.788-3.9t2.137-3.175q1.35-1.35 3.175-2.137T12 2q2.075 0 3.9.788t3.175 2.137q1.35 1.35 2.138 3.175T22 12q0 2.075-.788 3.9t-2.137 3.175q-1.35 1.35-3.175 2.138T12 22m0-2q3.35 0 5.675-2.325T20 12q0-3.35-2.325-5.675T12 4Q8.65 4 6.325 6.325T4 12q0 3.35 2.325 5.675T12 20m0-8'/></svg>` : `<svg class='absolute inset-y-0 right-4 my-auto text-green' xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24'><path fill='currentColor' d='m10.5 16.2l-4-4l1.4-1.4l2.6 2.6l5.6-5.6l1.4 1.4l-7 7Z'/></svg>`"
                    :input-class="{
                      'formkit-input': false,
                    }"
                    label-class="text-md block font-medium capitalize mb-2"
                    :classes="{
                      message: 'text-red-500 text-sm',
                      outer: 'block w-full transition duration-200',
                      wrapper: '!max-w-full w-full',
                      inner: `!shadow-none relative`,
                      input: `h-[55px] min-h-[55px] !leading-[1.6] !pr-10 w-[99.5%] mx-auto flex !border !border-primary/10 focus:!border-secondary-red-100/80 hover:!border-secondary-red-100/80 !placeholder-slate-300 !py-3 !px-4 !text-primary !shadow-sm !bg-white !text-md !rounded-md focus:!ring-offset-1 focus:!ring-2 focus:!ring-red-500/20 focus:!border-red-500`,
                    }"
                  />
                </div>
                <li v-else class="md:justify-normal relative z-10 w-full flex flex-col cursor-pointer justify-center rounded-lg p-4 transition duration-200 ease-linear md:flex-row md:items-center">
                  <UISpinner class="mx-auto min-h-[135px]" />
                </li>
              </template>
            </CardQuestions>
          </div>
        </div>
      </div>
      <div class="mb-6 flex items-start">
        <div class="flex items-center">
          <FormKit
            id="terms"
            required
            v-model="acceptPolicy"
            type="checkbox"
            validation=""
            validation-visibility="live"
            :classes="{
              input: 'h-4.5 w-4.5 border border-primary/10 rounded-md bg-white accent-secondary-red-100'
            }"
        />
        </div>
        <AcceptPolicy :first-text="$t('career.form.ihave')" :last-text="$t('career.form.privacy')" />
      </div>
      <FormKit
        type="submit"
        :label="$t('button.submit_app')"
        :disabled="disabled"
        :input-class="{
          'formkit-input': false,
        }"
        :classes="{
          outer: '!mb-0 ',
          input: 'bg-red-gradient text-md whitespace-nowrap rounded-lg px-6 py-3 font-medium leading-6 transition-shadow text-white ring-secondary-red-100/25 transition duration-200 ease-in-out hover:ring-4 disabled:cursor-not-allowed disabled:opacity-50',
        }"
      />
      <p v-if="captchaVerified" class="mt-3 text-sm text-blue-600">
        <span>reCAPTCHA verified!</span>
      </p>
    </FormKit>
  </div>
  <UIAlert v-model="alertActive" theme="success" :title="$t('alert.success.title')" :description="$t('alert.success.description')" />
  <UIAlert v-model="alertError" theme="error" :title="$t('alert.error.title')" :description="uploadFailed ? $t('alert.error.file_description') : $t('alert.error.description')" />
</template>

<style>
.formkit-custom-input ul.formkit-file-list li {
  @apply flex flex-col items-start;
}

.formkit-custom-input ul.formkit-file-list {
  @apply pl-0;
}

.formkit-custom-input button.formkit-file-remove {
  @apply font-bold cursor: pointer z-10 relative mt-2 hover:text-secondary-red-100;
}
</style>
